import React from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Home, 
  ArrowLeft, 
  Search, 
  FileQuestion,
  ChevronRight
} from 'lucide-react';

const NotFound = () => {
  const navigate = useNavigate();

  const handleGoHome = () => {
    navigate('/');
  };

  const handleGoBack = () => {
    navigate(-1);
  };

  const handleAdminLogin = () => {
    navigate('/admin');
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-primary-50 to-accent-50">
      <div className="max-w-4xl mx-auto px-4 py-8">
        {/* Header Section */}
        <div className="bg-gradient-to-r from-primary-600 to-primary-800 rounded-xl shadow-lg mb-8 p-8 relative overflow-hidden">
          <div className="relative z-10">
            <div className="flex items-center justify-between mb-4">
              <div className="bg-white bg-opacity-20 p-4 rounded-xl backdrop-blur-sm">
                <img 
                  src="/logo.png" 
                  alt="CC Jensen Logo" 
                  className="w-12 h-12 object-contain"
                  onError={(e) => {
                    e.target.style.display = 'none';
                    e.target.nextSibling.style.display = 'block';
                  }}
                />
                <FileQuestion className="w-12 h-12 text-white hidden" />
              </div>
              <div className="text-white text-center flex-1 mx-8">
                <h1 className="text-3xl font-bold">Page Not Found</h1>
                <p className="text-primary-100 mt-1">The page you're looking for doesn't exist</p>
              </div>
              <div className="bg-white bg-opacity-20 p-4 rounded-xl backdrop-blur-sm">
                <img 
                  src="/logo2.png" 
                  alt="CC Jensen Logo 2" 
                  className="w-12 h-12 object-contain"
                  onError={(e) => {
                    e.target.style.display = 'none';
                    e.target.nextSibling.style.display = 'block';
                  }}
                />
                <FileQuestion className="w-12 h-12 text-white hidden" />
              </div>
            </div>
          </div>
        </div>

        {/* 404 Content */}
        <div className="bg-white rounded-xl shadow-lg border border-gray-100 overflow-hidden">
          <div className="p-8 text-center">
            {/* Large 404 Number */}
            <div className="mb-8">
              <h2 className="text-8xl md:text-9xl font-bold bg-gradient-to-r from-primary-600 to-primary-800 bg-clip-text text-transparent leading-none">
                404
              </h2>
              <div className="w-24 h-1 bg-gradient-to-r from-primary-500 to-accent-500 mx-auto mt-4 rounded-full"></div>
            </div>

            {/* Error Message */}
            <div className="mb-8 space-y-4">
              <div className="flex items-center justify-center mb-6">
                <div className="p-4 bg-primary-50 rounded-full">
                  <Search className="w-8 h-8 text-primary-600" />
                </div>
              </div>
              
              <h3 className="text-2xl font-semibold text-gray-800 mb-3">
                Oops! Page not found
              </h3>
              
              <p className="text-gray-600 text-lg max-w-2xl mx-auto leading-relaxed">
                The page you're looking for might have been moved, deleted, or you entered the wrong URL.
                Don't worry, it happens to the best of us!
              </p>
            </div>

            {/* Action Buttons */}
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mb-8">
              <button
                onClick={handleGoHome}
                className="flex items-center gap-3 px-8 py-4 bg-gradient-to-r from-primary-600 to-primary-700 text-white rounded-xl hover:from-primary-700 hover:to-primary-800 font-medium shadow-md hover:shadow-lg transition-all duration-200 transform hover:scale-105"
              >
                <Home className="w-5 h-5" />
                <span>Go to Homepage</span>
              </button>
              
              <button
                onClick={handleGoBack}
                className="flex items-center gap-3 px-8 py-4 bg-white text-gray-700 rounded-xl hover:bg-gray-50 border border-gray-200 font-medium shadow-md hover:shadow-lg transition-all duration-200"
              >
                <ArrowLeft className="w-5 h-5" />
                <span>Go Back</span>
              </button>
            </div>

            {/* Quick Links */}
            <div className="bg-gradient-to-r from-gray-50 to-gray-100 rounded-xl p-6 border border-gray-200">
              <h4 className="font-semibold text-gray-800 mb-4 flex items-center justify-center gap-2">
                <div className="p-1 bg-gray-200 rounded-lg">
                  <ChevronRight className="w-4 h-4 text-gray-700" />
                </div>
                Quick Links
              </h4>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                <button
                  onClick={handleGoHome}
                  className="flex items-center justify-between p-3 bg-white rounded-lg hover:bg-primary-50 hover:text-primary-700 transition-colors border border-gray-200"
                >
                  <span className="font-medium">CC Jensen System Inquiry</span>
                  <ChevronRight className="w-4 h-4" />
                </button>
                
                <button
                  onClick={handleAdminLogin}
                  className="flex items-center justify-between p-3 bg-white rounded-lg hover:bg-primary-50 hover:text-primary-700 transition-colors border border-gray-200"
                >
                  <span className="font-medium">Admin Login</span>
                  <ChevronRight className="w-4 h-4" />
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Additional Help Section */}
        <div className="mt-8 text-center">
          <div className="bg-white rounded-xl shadow-lg border border-gray-100 p-6">
            <div className="flex items-center justify-center gap-3 mb-4">
              <div className="p-2 bg-accent-50 rounded-lg">
                <FileQuestion className="w-6 h-6 text-accent-600" />
              </div>
              <h3 className="text-lg font-semibold text-gray-800">Need Help?</h3>
            </div>
            
            <p className="text-gray-600 text-sm mb-4 max-w-lg mx-auto">
              If you believe this is an error or need assistance with our filtration systems, 
              please don't hesitate to contact us.
            </p>
            
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4 text-sm">
              <div className="flex items-center gap-2 text-gray-600">
                <span className="font-medium">Email:</span>
                <a href="mailto:sales@mhs.com.au" className="text-primary-600 hover:text-primary-700 transition-colors">
                  sales@mhs.com.au
                </a>
              </div>
              
              <div className="flex items-center gap-2 text-gray-600">
                <span className="font-medium">Phone:</span>
                <a href="tel:+61892492511" className="text-primary-600 hover:text-primary-700 transition-colors">
                  (+61 8) 9249 2511
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default NotFound;
